/**
 * Family contribution stacked bar.
 *
 * Shows three side-by-side numbers — Partner 1's family, Partner 2's family,
 * Joint — with a horizontal stacked-bar visualisation of:
 *   • the family budget (planned)
 *   • the spent so far (from paid payment-schedule installments tagged with
 *     `paid_by_family`, falling back to `responsible_family` when paid_by
 *     hasn't been recorded).
 *
 * Hidden when no family-budget split is configured (legacy / single-funder
 * weddings) so we don't add chart noise for users who don't need it.
 */
import React, { useMemo } from 'react';
import { Card } from '../ui/card';

const FAMILY_COLORS = {
  partner1: '#D1B0AB', // dusty rose (brand accent)
  partner2: '#9D8089', // deeper mauve
  joint:    '#D4B896', // warm beige
};

function fmtMoney(n, currency) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);
}

function partnerLabel(wedding, key) {
  if (key === 'joint') return 'Joint';
  const name = wedding?.[`${key}_name`];
  return name ? `${name}'s family` : key === 'partner1' ? "Partner 1's family" : "Partner 2's family";
}

function StackedBar({ items, total }) {
  // Sums above 0; otherwise render a single empty pill so the layout doesn't
  // collapse.
  const safeTotal = total > 0 ? total : 1;
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-border">
      {items.map((it) => {
        const pct = (it.value / safeTotal) * 100;
        if (pct <= 0) return null;
        return (
          <div
            key={it.key}
            style={{ width: `${pct}%`, background: FAMILY_COLORS[it.key] }}
            data-testid={`family-bar-segment-${it.key}`}
          />
        );
      })}
    </div>
  );
}

export default function FamilyContributionCard({ wedding, payments, currency }) {
  // Sum payment installments by family — paid_by takes precedence over
  // responsible_family for "actually contributed". For "expected
  // commitment" we use top-level family budgets the bride entered in
  // settings/onboarding.
  const spentByFamily = useMemo(() => {
    const out = { partner1: 0, partner2: 0, joint: 0, unassigned: 0 };
    for (const p of payments || []) {
      const planLevelResp = p.responsible_family;
      const planLevelPaidBy = p.paid_by_family;
      for (const inst of p.installments || []) {
        if (!inst.paid) continue;
        const family =
          inst.paid_by_family || planLevelPaidBy ||
          inst.responsible_family || planLevelResp ||
          'unassigned';
        const amt = Number(inst.amount) || 0;
        if (family in out) out[family] += amt;
        else out.unassigned += amt;
      }
    }
    return out;
  }, [payments]);

  const partner1Budget = Number(wedding?.partner1_family_budget) || 0;
  const partner2Budget = Number(wedding?.partner2_family_budget) || 0;
  const jointBudget    = Number(wedding?.joint_budget) || 0;
  const totalBudget    = partner1Budget + partner2Budget + jointBudget;
  const totalSpent     = spentByFamily.partner1 + spentByFamily.partner2 + spentByFamily.joint;

  // Hide entirely if no family-budget split has been configured AND no
  // family-tagged payments exist yet. Avoids chart noise for legacy
  // single-funder weddings.
  if (totalBudget === 0 && totalSpent === 0) return null;

  const rows = [
    { key: 'partner1', label: partnerLabel(wedding, 'partner1'), budget: partner1Budget, spent: spentByFamily.partner1 },
    { key: 'partner2', label: partnerLabel(wedding, 'partner2'), budget: partner2Budget, spent: spentByFamily.partner2 },
    { key: 'joint',    label: partnerLabel(wedding, 'joint'),    budget: jointBudget,    spent: spentByFamily.joint },
  ];

  return (
    <Card className="card-surface p-6 space-y-5" data-testid="family-contribution-card">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-charcoal">Family contributions</h2>
          <p className="text-sm text-muted mt-1">
            Who's funding what — split across both sides plus the joint pot.
          </p>
        </div>
        {totalBudget > 0 && (
          <div className="text-right">
            <p className="font-mono text-xs uppercase tracking-wider text-muted">Total committed</p>
            <p className="font-serif text-2xl text-charcoal">{fmtMoney(totalBudget, currency)}</p>
          </div>
        )}
      </div>

      {totalBudget > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Committed budget split</p>
          <StackedBar
            items={rows.map((r) => ({ key: r.key, value: r.budget }))}
            total={totalBudget}
          />
        </div>
      )}

      {totalSpent > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Paid so far</p>
          <StackedBar
            items={rows.map((r) => ({ key: r.key, value: r.spent }))}
            total={totalSpent}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
        {rows.map((r) => {
          const pct = r.budget > 0 ? Math.min(100, Math.round((r.spent / r.budget) * 100)) : 0;
          return (
            <div
              key={r.key}
              className="border border-border rounded-sm p-3 space-y-1"
              data-testid={`family-card-${r.key}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: FAMILY_COLORS[r.key] }}
                  aria-hidden="true"
                />
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
                  {r.label}
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="font-serif text-lg text-charcoal">{fmtMoney(r.spent, currency)}</span>
                <span className="font-mono text-[10px] text-muted">paid</span>
              </div>
              {r.budget > 0 && (
                <p className="text-xs text-muted">
                  of {fmtMoney(r.budget, currency)} committed · {pct}%
                </p>
              )}
            </div>
          );
        })}
      </div>

      {spentByFamily.unassigned > 0 && (
        <p className="text-xs text-muted" data-testid="family-unassigned-note">
          {fmtMoney(spentByFamily.unassigned, currency)} paid without a family tag — set "Responsible" or "Paid by" on a payment to attribute it.
        </p>
      )}
    </Card>
  );
}
